/**
 * Audit event store · P2.6.a · zustand
 *
 * Holds the live stream of WS events from WorkspaceBus + REST gap-fill events.
 * Components subscribe to slices (by session · by kind) for chat panel ·
 * canvas updates · monitoring tab.
 *
 * Invariants:
 *   - Events de-duplicated by audit_id (gap-fill REST + WS push may overlap)
 *   - Two cursors tracked separately:
 *       * `highWaterAuditId` = max(seen) · for UI heartbeat / "you are here"
 *       * `replayCursor`     = last contiguous audit_id confirmed by gap-fill ·
 *                              this is what `since_audit_id` should use on the
 *                              NEXT reconnect (codex R2 P1 fix · prior version
 *                              used highWater as resume · live events would
 *                              advance it past unfilled gaps and the gap was
 *                              lost forever).
 *   - `workspaceId` tracked in state · hook resets on workspace switch
 *     (codex R2 P1 fix · prior version leaked dedup + cursor across tenants).
 *
 * codex-review 2026-04-29 R1 P2 fix · dedup is now O(1) via a parallel
 * `Set<audit_id>` · prior O(N) `events.some()` hot path was unbounded as
 * events grew during a long session.
 */

import { create } from 'zustand';
import type { WsEvent } from '../lib/ws-client';

interface AuditState {
  /** Workspace this store currently holds events for. Empty string = unbound. */
  workspaceId: string;
  events: WsEvent[];
  /** Mirror of all known audit_ids · O(1) dedup. */
  seenAuditIds: Set<number>;
  /** Max audit_id seen · drives UI badge / sort. */
  highWaterAuditId: number;
  /** Last contiguous audit_id where gap-fill confirmed completeness. The next
   * REST gap-fill should resume from here · NOT from highWaterAuditId · so a
   * paginated drain that was capped doesn't get bypassed by subsequent live
   * events. (codex R2 P1) */
  replayCursor: number;
  /** Connection status from WS client · drives UI badge. */
  connectionState: 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';
}

interface AuditActions {
  /** Insert a single event · de-dup by audit_id. */
  pushEvent: (evt: WsEvent) => void;
  /** Bulk insert (gap-fill REST replay) · de-dup. */
  pushEvents: (evts: WsEvent[]) => void;
  /** Advance the replay cursor (gap-fill confirmed contiguous to this id). */
  setReplayCursor: (auditId: number) => void;
  setConnectionState: (s: AuditState['connectionState']) => void;
  /** Bind store to a workspace. If different from current · resets all state. */
  bindWorkspace: (workspaceId: string) => void;
  reset: () => void;
}

const emptyState = (workspaceId = ''): AuditState => ({
  workspaceId,
  events: [],
  seenAuditIds: new Set<number>(),
  highWaterAuditId: 0,
  replayCursor: 0,
  connectionState: 'idle',
});

export const useAuditStore = create<AuditState & AuditActions>((set) => ({
  ...emptyState(),
  pushEvent: (evt) =>
    set((st) => {
      const audit_id = evt.audit_id ?? 0;
      // Dedup by audit_id · keep first
      if (audit_id > 0 && st.seenAuditIds.has(audit_id)) {
        return st;
      }
      const seen = new Set(st.seenAuditIds);
      if (audit_id > 0) seen.add(audit_id);
      return {
        events: [...st.events, evt],
        seenAuditIds: seen,
        highWaterAuditId: Math.max(st.highWaterAuditId, audit_id),
      };
    }),
  pushEvents: (evts) =>
    set((st) => {
      const seen = new Set(st.seenAuditIds);
      const fresh: WsEvent[] = [];
      let newHigh = st.highWaterAuditId;
      for (const e of evts) {
        const id = e.audit_id;
        if (typeof id !== 'number' || id <= 0) {
          fresh.push(e); // no id · always include
          continue;
        }
        if (seen.has(id)) continue;
        seen.add(id);
        if (id > newHigh) newHigh = id;
        fresh.push(e);
      }
      if (fresh.length === 0) return st;
      return {
        events: [...st.events, ...fresh],
        seenAuditIds: seen,
        highWaterAuditId: newHigh,
      };
    }),
  setReplayCursor: (auditId) =>
    set((st) => ({
      replayCursor: Math.max(st.replayCursor, auditId),
    })),
  setConnectionState: (s) => set({ connectionState: s }),
  bindWorkspace: (workspaceId) =>
    set((st) => {
      if (st.workspaceId === workspaceId) return st;
      // Workspace switch · drop all state to avoid cross-tenant leakage
      // (codex R2 P1 fix).
      return emptyState(workspaceId);
    }),
  reset: () => set(emptyState()),
}));

/**
 * Selector helpers · components call these instead of subscribing to whole store
 * to avoid unnecessary re-renders.
 */
export const selectChatEvents = (state: AuditState): WsEvent[] =>
  state.events.filter((e) => e.kind === 'chat.send');

export const selectCanvasEvents = (state: AuditState): WsEvent[] =>
  state.events.filter((e) => e.kind.startsWith('canvas.'));

export const selectSessionEvents = (sessionId: string) => (state: AuditState): WsEvent[] =>
  state.events.filter((e) => e.session_id === sessionId);
